import { useEffect, useState, useContext } from 'react';
import Cookies from 'js-cookie';
import { doc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { bbox, lineDistance, lineString } from '../utils/map';
import { getControlById, getPrevisionInicioDistancia } from '../utils/time';
import SharePanel from '../components/SharePanel';
import TopHeaderMenu from '../components/TopHeaderMenu';
import MsgNetLoading from '../components/MsgNetLoading';
import ResultadoFavoritosList from '../components/ResultadoFavoritosList';
import CompeticionHeaderMenu from '../components/CompeticionHeaderMenu';
import { FirebaseContext } from '../index';

function FavoritosScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { db } = useContext(FirebaseContext);

  const [evento, setEvento] = useState(null);
  const [competicion, setCompeticion] = useState({
    mapa_geojson: null,
    mapa_visible: null
  });
  const [competicionId, setCompeticionId] = useState(null);
  const [sectores, setSectores] = useState([]);
  const [sectoresFiltrados, setSectoresFiltrados] = useState([]);
  const [geoJson, setGeoJson] = useState(null);
  const [turfLine, setTurfLine] = useState(null);
  const [turfBounds, setTurfBounds] = useState(null);
  const [turfDistanceRelation, setTurfDistanceRelation] = useState(1.0);
  const [controles, setControles] = useState([]);
  const [controlMeta, setControlMeta] = useState({
    distancia: 0
  });
  const [resultados, setResultados] = useState([]);
  const [showSharePanel, setShowSharePanel] = useState(false);

  const toogleSharePanel = () => {
    setShowSharePanel(!showSharePanel);
  };

  const onClickBack = () => {
    navigate(`/${competicion.evento_id}`);
  };

  /**
   * Listen competición updates depending on route changes
   */
  useEffect(() => {
    const competicionId = parseInt(params.competicionId, 10);

    if (competicionId) {
      const competicionRef = doc(db, 'competicion', String(competicionId));

      return onSnapshot(competicionRef, (snapshot) => {
        let competicion = snapshot.exists() ? snapshot.data() : null;
        if (competicion) {
          competicion.id = snapshot.id;
        }
        setCompeticion(competicion);
        setCompeticionId(parseInt(competicion.id, 10));
      });
    }
  }, [params.competicionId, db]);

  /**
   * Listen event updates depending on competition changes
   */
  useEffect(() => {
    if (competicion && competicion.evento_id) {
      const eventoId = competicion.evento_id;

      if (eventoId) {
        const eventoRef = doc(db, 'evento', String(eventoId));

        return onSnapshot(eventoRef, (snapshot) => {
          let evento = snapshot.exists() ? snapshot.data() : null;
          if (evento) {
            evento.id = snapshot.id;
          }
          setEvento(evento);
        });
      }
    }
  }, [competicion, db]);

  /**
   * Listen control changes depending on competition changes
   */
  useEffect(() => {
    if (!competicionId) return;

    const controlesQuery = query(
      collection(db, 'control'),
      where('competicion_id', '==', competicionId),
      where('visible', '==', true),
      orderBy('distancia', 'asc')
    );

    return onSnapshot(controlesQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setControles(data);

      let meta = data.filter(c => c.tipo === 'META');
      if (meta.length) {
        setControlMeta(meta[0]);
      } else {
        setControlMeta(null);
      }
    });
  }, [competicionId, db]);

  /**
   * Listen sector changes depending on competition changes
   */
  useEffect(() => {
    if (!competicionId) return;

    const sectoresQuery = query(
      collection(db, 'sector'),
      where('competicion_id', '==', competicionId),
      orderBy('inicio', 'asc')
    );

    return onSnapshot(sectoresQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSectores(data);
    });
  }, [competicionId, db]);

  /**
   * Listen the result updates of the favorite participants
   */
  useEffect(() => {
    if (!competicionId) return;

    const favoritosCookie = Cookies.get('favoritos-' + competicionId);
    let favoritos = [];

    if (favoritosCookie) {
      try {
        favoritos = JSON.parse(favoritosCookie);
      } catch (e) {
        console.error('Error parsing favoritos cookie:', e);
      }
    }

    if (favoritos && favoritos.length) {
      const unlistenList = [];

      favoritos.forEach(dorsal => {
        dorsal = parseInt(dorsal, 10);
        const resultadoRef = doc(db, 'resultado', String(competicionId) + '-' + String(dorsal));

        unlistenList.push(
          onSnapshot(resultadoRef, (snapshot) => {
            const doc = snapshot.exists() ? snapshot.data() : null;

            setResultados(prevResultados => {
              const newResultados = [...prevResultados];
              const encontrado = newResultados.find(a => parseInt(a.dorsal, 10) === dorsal);

              if (doc) {
                if (encontrado) {
                  const index = newResultados.indexOf(encontrado);
                  newResultados[index] = doc;
                } else {
                  newResultados.push(doc);
                }
              } else {
                if (encontrado) {
                  const index = newResultados.indexOf(encontrado);
                  newResultados.splice(index, 1);
                }
              }

              return newResultados;
            });
          })
        );
      });

      return () => {
        unlistenList.forEach(f => f());
      };
    } else {
      setResultados([]);
    }
  }, [competicionId, db]);

  /**
   * Build the map rendering data
   */
  useEffect(() => {
    if (competicion && competicion.mapa_visible) {
      if (competicion.mapa_geojson && controles && controles.length) {
        const data = JSON.parse(competicion.mapa_geojson);
        const coordsGeo = data.features[0].geometry.coordinates.map(
          r => [r[1], r[0]]
        );
        const _turfLine = lineString(coordsGeo);
        const _turfBounds = bbox(_turfLine);
        let turfDistance = lineDistance(_turfLine) * 1000.0;

        const distanciaInicio = getPrevisionInicioDistancia(
          competicion, controles
        );
        if (controlMeta && turfDistance && turfDistance > 0) {
          const calculatedRelation = (controlMeta.distancia - distanciaInicio) / turfDistance;
          setTurfDistanceRelation(isFinite(calculatedRelation) ? calculatedRelation : 1.0);
        }
        setGeoJson(coordsGeo);
        setTurfLine(_turfLine);
        setTurfBounds(_turfBounds);
        setSectoresFiltrados([]);
      } else if (sectores && sectores.length > 0) {
        const sectoresConMapa = sectores.filter(s => s.mapa_geojson);

        if (sectoresConMapa.length > 0) {
          const primerGeo = JSON.parse(sectoresConMapa[0].mapa_geojson);
          const primerPunto = JSON.stringify(primerGeo.features[0].geometry.coordinates[0]);
          const ultimoPunto = JSON.stringify(primerGeo.features[0].geometry.coordinates[primerGeo.features[0].geometry.coordinates.length - 1]);

          const todosMismoMapa = sectoresConMapa.every(s => {
            const geo = JSON.parse(s.mapa_geojson);
            const p1 = JSON.stringify(geo.features[0].geometry.coordinates[0]);
            const p2 = JSON.stringify(geo.features[0].geometry.coordinates[geo.features[0].geometry.coordinates.length - 1]);
            return p1 === primerPunto && p2 === ultimoPunto;
          });

          if (todosMismoMapa) {
            const data = primerGeo;
            const coordsGeo = data.features[0].geometry.coordinates.map(
              r => [r[1], r[0]]
            );
            const _turfLine = lineString(coordsGeo);
            const _turfBounds = bbox(_turfLine);
            let turfDistance = lineDistance(_turfLine) * 1000.0;

            const distanciaInicio = getPrevisionInicioDistancia(
              competicion, controles
            );
            if (controlMeta && turfDistance && turfDistance > 0) {
              const calculatedRelation = (controlMeta.distancia - distanciaInicio) / turfDistance;
              setTurfDistanceRelation(isFinite(calculatedRelation) ? calculatedRelation : 1.0);
            }
            setGeoJson(coordsGeo);
            setTurfLine(_turfLine);
            setTurfBounds(_turfBounds);
            setSectoresFiltrados([]);
          } else {
            let _totalLine = [];
            const _turfLine = [];

            const _sectores = sectoresConMapa.map((s) => {
              let geo = JSON.parse(s.mapa_geojson);
              geo = geo.features[0].geometry.coordinates.map(
                r => [r[1], r[0]]
              );
              const line = lineString(geo);
              _turfLine.push(line);
              s.distanciaGeo = lineDistance(line) * 1000.0;
              _totalLine = _totalLine.concat(geo);

              s.inicio_metros = 0.0;
              if (s.inicio) {
                const inicio = getControlById(controles, s.inicio);
                if (inicio && typeof inicio.distancia === 'number') {
                  s.inicio_metros = inicio.distancia;
                }
              }

              const fin = getControlById(controles, s.fin);
              if (fin && typeof fin.distancia === 'number') {
                s.fin_metros = fin.distancia;
              } else {
                s.fin_metros = s.inicio_metros;
              }

              s.distancia = s.fin_metros - s.inicio_metros;

              if (s.distanciaGeo && s.distanciaGeo > 0 && s.distancia > 0) {
                s.turfDistanceRelation = s.distancia / s.distanciaGeo;
              } else {
                s.turfDistanceRelation = 1.0;
              }

              if (!isFinite(s.turfDistanceRelation)) {
                s.turfDistanceRelation = 1.0;
              }

              return s;
            });

            if (_totalLine.length > 0) {
              const _turfBounds = bbox(lineString(_totalLine));
              setTurfBounds(_turfBounds);
            }

            setTurfLine(_turfLine);
            setGeoJson(_sectores.map(s => {
              let geo = JSON.parse(s.mapa_geojson);
              return geo.features[0].geometry.coordinates.map(r => [r[1], r[0]]);
            }));
            setSectoresFiltrados(_sectores);
          }
        }
      }
    }
  }, [competicion, controlMeta, controles, sectores]);

  if (!competicion || competicion.visible === undefined) {
    return <MsgNetLoading />;
  }

  return (
    <div>
      <TopHeaderMenu
        onClickBack={onClickBack}
        onClickShare={toogleSharePanel}
        competicion={competicion}
        evento={evento}
      />
      <CompeticionHeaderMenu
        competicion={competicion}
        selected={3}
        evento={evento}
      />
      <div className="pt-28">
        <ResultadoFavoritosList
          resultados={resultados}
          competicion={competicion}
          controles={controles}
          sectores={sectoresFiltrados}
          geoJson={geoJson}
          turfLine={turfLine}
          turfBounds={turfBounds}
          turfDistanceRelation={turfDistanceRelation}
        />

        {showSharePanel && (
          <SharePanel
            close={toogleSharePanel}
            url={`/competicion/${competicion.id}/favoritos`}
            title={`Progreso de tus favoritos en vivo en la prueba ${competicion.nombre} en ${evento.nombre}`}
          />
        )}
      </div>
    </div>
  );
}

export default FavoritosScreen;